import { z } from 'zod'

/**
 * Phone-number policy shared by every signup surface (the register wizard and
 * the social-signup completion step).
 *
 * Deliberately permissive about SHAPE and strict only about substance. It used
 * to be `/^[\d\s]+$/` — digits and spaces — which refused every normal way of
 * writing a number: `(34) 99999-9999`, `+55 34 99999-9999`, `+1 (415) 555-0142`.
 * A browser autofilling a `tel` field emits the international form, so the most
 * common way of reaching this field was rejected with "only numbers are
 * allowed" and nothing on screen said which character to delete.
 *
 * What the number becomes is not this schema's business: the API's
 * `normalizePhoneForWhmcs` strips the punctuation WHMCS refuses and the country
 * calling code WHMCS would otherwise store twice.
 */
export const PHONE_SHAPE = /^\+?[\d\s().-]+$/

/** E.164 allows at most 15 digits; the shortest national numbers are 8. */
export const PHONE_MIN_DIGITS = 8
export const PHONE_MAX_DIGITS = 15

export function phoneDigitCount(value: string): number {
  return value.replace(/\D/g, '').length
}

export function isPhonePlausible(value: string): boolean {
  if (!PHONE_SHAPE.test(value.trim())) return false
  const digits = phoneDigitCount(value)
  return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS
}

/** Zod schema for a signup phone number, with a translated message. */
export function createPhoneSchema(t: (key: string) => string) {
  const message = t('page.register.phoneInvalid')
  return z.string()
    .regex(PHONE_SHAPE, message)
    .refine(value => isPhonePlausible(value), message)
}
